import { sb } from '../supabase';

// Contact violations — every time the platform masked a phone number, WeChat,
// email or link someone tried to send (owner, 2026-10-15: «كيف الانذار يوصل
// للادمن ؟» → a page plus a count in the admin Notifications, no email).
//
// The rows are written only by the masking triggers (migration
// 20261014163345_contact_masking_2_chat, ..._3_supplier_text) into
// public.contact_masking_events. Reading and deciding go through two
// admin-only SECURITY DEFINER RPCs (20261014163439_contact_masking_4_admin):
//   admin_list_contact_events(p_status, p_limit)
//   admin_review_contact_event(p_id, 'dismissed' | 'warned')
// 'warned' also sends the user the trilingual `contact_warning` notification;
// 'dismissed' marks a false positive and stops it counting toward their level.
//
// ⚠️ ONE PREDICATE, TWO PLACES. "Waiting for review" is review_status = 'open'.
// listViolations('open') asks the RPC for it, fetchOpenViolationCount() asks
// the table for it (head count, for the sidebar badge and the Reviews tile).
// They must always test the same thing — a badge that counts a different set
// than the page it opens is a bug that looks like a data problem (the
// isReviewable / fetchReadyCount lesson, CLAUDE.md golden rule 2a).
export const OPEN = 'open';

export async function listViolations(status = OPEN, limit = 200) {
  const { data, error } = await sb.rpc('admin_list_contact_events', { p_status: status, p_limit: limit });
  if (error) throw error;
  return data || [];
}

export async function fetchOpenViolationCount() {
  const { count, error } = await sb.from('contact_masking_events')
    .select('id', { count: 'exact', head: true })
    .eq('review_status', OPEN);
  if (error) throw error;
  return count || 0;
}

export async function reviewViolation(id, action) {
  const { error } = await sb.rpc('admin_review_contact_event', { p_id: id, p_action: action });
  if (error) throw error;
}

const SOURCE = {
  message:                { ar: 'محادثة مع مورد', en: 'Supplier chat' },
  factory_thread_message: { ar: 'محادثة مع مصنع', en: 'Factory chat' },
  market_inquiry_message: { ar: 'سؤال لمورد في السوق', en: 'Store supplier question' },
  product:                { ar: 'منتج', en: 'Product' },
  profile:                { ar: 'ملف المورد', en: 'Supplier profile' },
};
export const sourceLabel = (source, isAr) => {
  const s = SOURCE[source];
  return s ? (isAr ? s.ar : s.en) : String(source || '');
};

export const isSupplierRole = (role) => String(role || '').toLowerCase() === 'supplier';

export const personName = (e) => {
  const n = String(e?.user_company || e?.user_name || '').trim();
  return n || (isSupplierRole(e?.user_role) ? 'مورّد' : 'تاجر');
};

// /admin2/suppliers/:id is keyed by a CATALOG FACTORY id, not a profile, so a
// registered supplier opens on its file page, /admin2/verification/:profileId —
// the same door ConsoleRequestDetail and ConsoleSuppliers use.
export const personLink = (e) => {
  if (!e?.user_id) return null;
  return isSupplierRole(e.user_role) ? `/admin2/verification/${e.user_id}` : `/admin2/traders/${e.user_id}`;
};

// A buyer↔supplier chat opens the pair in /admin2/conversations, which is keyed
// trader + supplier whichever of the two sent the masked message.
export const conversationLink = (e) => {
  if (e?.source !== 'message' || !e.user_id || !e.counterpart_id) return null;
  const supplierSent = isSupplierRole(e.user_role);
  const q = new URLSearchParams({
    channel: 'supplier',
    trader: supplierSent ? e.counterpart_id : e.user_id,
    counterpart: supplierSent ? e.user_id : e.counterpart_id,
  });
  return `/admin2/conversations?${q}`;
};

// One event -> one item in the admin Notifications feed. It carries
// `violationId`, deliberately NOT `id`: in that feed `id` means an admin_events
// row, and its «تم التعامل» button calls admin_mark_event_handled with it.
// A violation is settled on its own page (dismiss / warn), never there.
export function violationToItem(e) {
  if (!e?.id) return null;
  const repeat = Number(e.level) >= 2;
  const who = personName(e);
  return {
    violationId: e.id,
    group: 'violation',
    type: 'violation',
    at: e.created_at,
    handled: e.review_status !== OPEN,
    title_ar: `مخالفة تواصل${repeat ? ' (تكرار)' : ''} — ${who}`,
    title_en: `Contact violation${repeat ? ' (repeat)' : ''} — ${who}`,
    sub: [sourceLabel(e.source, true), String(e.masked || '').slice(0, 80)].filter(Boolean).join(' · '),
    link: '/admin2/contact-violations',
    tone: repeat ? 'danger' : 'warn',
  };
}
